import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../api/axios';

const CompanyContext = createContext(null);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export const CompanyProvider = ({ children }) => {
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCompanySettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/company-settings');
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Failed to fetch company settings:', error);
      // Set default values if fetch fails
      setCompanySettings({
        company_name: 'Ecommerce',
        currency: 'MAD',
        currency_symbol: 'DH',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch company settings if user is authenticated
    const token = localStorage.getItem('token');
    if (token) {
      fetchCompanySettings();
    } else {
      setCompanySettings({
        company_name: 'Ecommerce',
        currency: 'MAD',
        currency_symbol: 'DH',
      });
      setLoading(false);
    }
  }, []);

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return `${companySettings?.currency_symbol || 'DH'}0.00`;
    const symbol = companySettings?.currency_symbol || 'DH';
    return `${parseFloat(amount).toFixed(2)} ${symbol}`;
  };

  const getCurrencySymbol = () => {
    return companySettings?.currency_symbol || 'DH';
  };

  const getCurrency = () => {
    return companySettings?.currency || 'MAD';
  };

  const getCompanyName = () => {
    return companySettings?.company_name || 'Ecommerce';
  };

  const getLogoUrl = () => {
    if (companySettings?.company_logo) {
      return `${companySettings.company_logo}`;
    }
    return null;
  };

  const updateCompanySettings = async (settings) => {
    try {
      const response = await api.put('/company-settings', settings);
      setCompanySettings(response.data.settings);
      return response.data;
    } catch (error) {
      console.error('Failed to update company settings:', error);
      throw error;
    }
  };

  const uploadLogo = async (file) => {
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const response = await api.post('/company-settings/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      await fetchCompanySettings(); // Refresh settings after logo upload
      return response.data;
    } catch (error) {
      console.error('Failed to upload logo:', error);
      throw error;
    }
  };

  const deleteLogo = async () => {
    try {
      const response = await api.delete('/company-settings/logo');
      await fetchCompanySettings(); // Refresh settings after logo delete
      return response.data;
    } catch (error) {
      console.error('Failed to delete logo:', error);
      throw error;
    }
  };

  const value = {
    companySettings,
    loading,
    formatCurrency,
    getCurrencySymbol,
    getCurrency,
    getCompanyName,
    getLogoUrl,
    updateCompanySettings,
    uploadLogo,
    deleteLogo,
    refreshSettings: fetchCompanySettings,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
};
